import { useEffect, useRef } from 'react';
import type { Icon, Marker as MarkerType } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { Text } from '@querodelivery/uni-react';
import { FOCUSED_PIN_Z_INDEX, POPUP_PREVIEW_DURATION } from './constants';
import type { MapPosition } from '../types';
import styles from './map-pin.module.scss';

interface MapPinParams {
  position: MapPosition;
  icon: Icon;
  label: string;
  isFocused: boolean;
  popupRevision: number;
  onClick?: () => void;
}

export const MapPin = ({
  position,
  icon,
  label,
  isFocused,
  popupRevision,
  onClick,
}: MapPinParams) => {
  const markerRef = useRef<MarkerType>(null);

  useEffect(() => {
    if (!isFocused || popupRevision === 0) {
      markerRef.current?.closePopup();
      return;
    }

    markerRef.current?.openPopup();

    const timeoutId = window.setTimeout(() => {
      markerRef.current?.closePopup();
    }, POPUP_PREVIEW_DURATION);

    return () => window.clearTimeout(timeoutId);
  }, [isFocused, popupRevision]);

  return (
    <Marker
      ref={markerRef}
      eventHandlers={{
        mouseover: () => markerRef.current?.openPopup(),
        mouseout: () => markerRef.current?.closePopup(),
        click: () => onClick?.(),
      }}
      position={position}
      icon={icon}
      zIndexOffset={isFocused ? FOCUSED_PIN_Z_INDEX : 0}
    >
      <Popup closeButton={false} autoPan={false}>
        <div className={styles.content}>
          <Text fontSize="xs" fontWeight="semibold" tone="highlight">
            {label}
          </Text>
        </div>
      </Popup>
    </Marker>
  );
};
